import { useState, useCallback, useEffect, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useRefresh } from "../contexts/RefreshContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtNum, fmtCur, fmtDate } from "../lib/format";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { MagnifyingGlass, Sparkle, CaretRight, CaretDown } from "@phosphor-icons/react";

const WINDOWS = [
  { label: "Next 7 days", days: 7 },
  { label: "Next 2 weeks", days: 14 },
  { label: "Next 30 days", days: 30 },
  { label: "Next 60 days", days: 60 },
  { label: "Next 90 days", days: 90 },
];

export default function ShipWindow() {
  const [sp, setSp] = useSearchParams();
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState("");
  const [item, setItem] = useState("");
  const [customer, setCustomer] = useState("");
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [askTable, setAskTable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const run = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        item: overrides.item ?? item,
        customer: overrides.customer ?? customer,
        days: overrides.days ?? days,
      };
      const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null));
      const r = await api.get("/ms/ship-window", { params: clean });
      setData(r.data);
      setOpen({});
    } finally {
      setLoading(false);
    }
  }, [item, customer, days]);

  const runAsk = useCallback(async (qArg) => {
    const q = (typeof qArg === "string" ? qArg : ask).trim();
    if (!q) return;
    setAsk(q);
    setAsking(true);
    setAnswer("");
    try {
      const r = await api.post("/ms/ask", { question: q });
      setAnswer(r.data.answer || "");
      setAskTable(r.data.table || null);
      const p = r.data.params || {};
      // reflect parsed params into the visible filters
      if (p.item != null) setItem(p.item || "");
      if (p.customer != null) setCustomer(p.customer || "");
      if (p.days) setDays(Number(p.days));
      setData(r.data.data || null);
      setOpen({});
    } catch (e) {
      setAnswer("Sorry, I couldn't answer that. Try the filters below.");
    } finally {
      setAsking(false);
    }
  }, [ask]);

  // When the global top-bar "Ask Order AI" routes here (…/ship-window?ask=<q>), auto-run the
  // question and render the full answer on this page, then clear the param so a refresh won't re-run.
  const askParam = sp.get("ask") || "";
  const { tick } = useRefresh();
  // Manual top-bar refresh → re-run the current ship-window query (bypasses cache via ?refresh=1).
  useEffect(() => { if (tick) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tick]);
  useEffect(() => {
    if (!askParam) return;
    runAsk(askParam);
    const next = new URLSearchParams(sp);
    next.delete("ask");
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askParam]);

  const exportRows = (data?.by_item || []).flatMap((g) =>
    (g.lines || []).map((l) => ({ ...l, _grp: g }))
  );
  const exportColumns = [
    { header: "Item #", value: (r) => r.icode || "" },
    { header: "WM Part #", value: (r) => r.wm_partno || "" },
    { header: "Description", value: (r) => r.name || "" },
    { header: "Customer", value: (r) => r.customer_name || "" },
    { header: "PO #", value: (r) => r.ms_pono || "" },
    { header: "Customer PO", value: (r) => r.ms_custpo || "" },
    { header: "Target Date", value: (r) => r.target_date || "" },
    { header: "Due Date", value: (r) => (r.deliv_date ? String(r.deliv_date).slice(0, 10) : "") },
    { header: "Open Qty", value: (r) => Number(r.qty_open) || 0 },
    { header: "Open Value", value: (r) => Number(r.open_value) || 0 },
  ];

  return (
    <Layout>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="ship-window-page" className="sw-page">
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 2px" }}>Ask</h1>
        <p className="muted" style={{ marginBottom: 18 }}>
          Ask anything about your open orders, shipments and sales — or use the ship-window tool below.
        </p>

        {/* Ask assistant */}
        <div style={{ border: "1px solid var(--omh-border, #e2e8f0)", borderRadius: 12, padding: 14, marginBottom: 18, background: "#ffffff", color: "#0f172a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
            <Sparkle size={16} weight="fill" color="#7c3aed" /> Ask
          </div>
          <div className="sw-ask-row" style={{ display: "flex", gap: 8 }}>
            <input
              data-testid="ship-ask-input"
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runAsk()}
              placeholder="Ask about orders, shipments, sales or track a container/invoice — e.g. where is container MSKU1234567? · which POs of item X shipped late?"
              style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#0f172a", fontSize: 14 }}
            />
            <button data-testid="ship-ask-btn" onClick={runAsk} disabled={asking || !ask.trim()}
              style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: asking ? "#a78bfa" : "#7c3aed", color: "white", fontWeight: 700, cursor: asking ? "wait" : "pointer" }}>
              {asking ? "Thinking…" : "Ask"}
            </button>
          </div>
          {answer && <div data-testid="ship-ask-answer" style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: "#0f172a", fontWeight: 500 }}>{answer}</div>}
          {askTable && askTable.rows && askTable.rows.length > 0 && (() => {
            const metricCol = askTable.columns[askTable.columns.length - 1];
            const metricNumeric = askTable.rows.some((r) => typeof r[metricCol] === "number")
              && askTable.rows.every((r) => r[metricCol] == null || typeof r[metricCol] === "number");
            return (
            <div data-testid="ship-ask-table" className="sw-table-wrap" style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 8, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", color: "#334155", textAlign: "left" }}>
                    {askTable.columns.map((c) => (
                      <th key={c} style={{ padding: "8px 12px", fontWeight: 700, textTransform: "capitalize", borderBottom: "1px solid #e2e8f0", textAlign: (c === metricCol && metricNumeric) ? "right" : "left" }}>
                        {c.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {askTable.rows.map((row, i) => {
                    return (
                      <tr key={i} style={{ background: i % 2 ? "#f8fafc" : "white", color: "#0f172a" }}>
                        {askTable.columns.map((c) => {
                          const v = row[c];
                          let disp = v;
                          if (c === metricCol && metricNumeric) {
                            if (c.includes("value")) disp = `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
                            else if (c.includes("qty")) disp = `${Number(v || 0).toLocaleString("en-IN")} pcs`;
                            else disp = Number(v || 0).toLocaleString("en-IN");
                          }
                          const alignRight = c === metricCol && metricNumeric;
                          return (
                            <td key={c} style={{ padding: "7px 12px", borderBottom: "1px solid #f1f5f9", textAlign: alignRight ? "right" : "left", fontWeight: alignRight ? 700 : 500 }}>
                              {(disp === null || disp === undefined || disp === "") ? "—" : disp}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>

        {/* Deterministic filters */}
        <div className="sw-filters" style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 16 }}>
          <Field label="Item / WM Part #">
            <input data-testid="ship-filter-item" value={item} onChange={(e) => setItem(e.target.value)} placeholder="code or WM part#" style={inp} />
          </Field>
          <Field label="Customer">
            <input data-testid="ship-filter-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="all customers" style={inp} />
          </Field>
          <Field label="Window">
            <select data-testid="ship-filter-window" value={days} onChange={(e) => setDays(Number(e.target.value))} style={inp}>
              {WINDOWS.map((w) => <option key={w.days} value={w.days}>{w.label}</option>)}
            </select>
          </Field>
          <button data-testid="ship-run-btn" className="sw-run" onClick={() => run()} disabled={loading}
            style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "var(--omh-primary, #0f766e)", color: "white", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <MagnifyingGlass size={15} /> {loading ? "Running…" : "Run"}
          </button>
          <div className="sw-grow" style={{ flex: 1 }} />
          <ExportXlsxButton filename="ship_window" sheetName="Ship Window" columns={exportColumns} rows={exportRows} testid="ship-export-btn" />
        </div>

        {/* Results */}
        {data && (
          <>
            <div className="sw-summary" style={{ display: "flex", flexWrap: "wrap", gap: 22, alignItems: "baseline", padding: "12px 16px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, marginBottom: 14 }} data-testid="ship-summary">
              <div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#0f766e" }}>{fmtNum(data.total_open_qty)}</div>
                <div className="muted" style={{ fontSize: 12 }}>pcs open{data.windowed === false ? "" : " to ship"}</div>
              </div>
              {data.total_open_value_by_ccy && Object.keys(data.total_open_value_by_ccy).length > 0 && (
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#334155" }} data-testid="ship-open-value">
                    {Object.entries(data.total_open_value_by_ccy).map(([c, v]) => `${c === "USD" ? "$" : c === "INR" ? "₹" : c + " "}${fmtNum(v)}`).join("  +  ")}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>open value</div>
                </div>
              )}
              <div className="muted" style={{ fontSize: 13 }}>
                {data.windowed === false
                  ? <>All open orders · {data.item_count} item(s) · {data.line_count} line(s)</>
                  : <>Target date <strong>{fmtDate(data.window?.from)} → {fmtDate(data.window?.to)}</strong> · {data.item_count} item(s) · {data.line_count} line(s)</>}
              </div>
            </div>

            <div style={{ overflowX: "auto", border: "1px solid var(--omh-border,#e2e8f0)", borderRadius: 10 }}>
              <table className="jde-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#1e293b", color: "white", textAlign: "left" }}>
                    <th style={th}></th><th style={th}>Item #</th><th style={th}>Description</th>
                    <th style={{ ...th, textAlign: "right" }}>Open Qty</th>
                    <th style={{ ...th, textAlign: "right" }}>Open Value</th>
                    <th style={{ ...th, textAlign: "right" }}>Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_item || []).map((g) => (
                    <Fragment key={g.icode}>
                      <tr data-testid={`ship-item-row-${g.icode}`} onClick={() => setOpen((o) => ({ ...o, [g.icode]: !o[g.icode] }))} style={{ cursor: "pointer", borderTop: "1px solid #e2e8f0" }}>
                        <td style={{ ...td, color: "#94a3b8" }}>{open[g.icode] ? <CaretDown size={13} /> : <CaretRight size={13} />}</td>
                        <td style={{ ...td, fontWeight: 700 }} className="text-mono">
                          {g.icode}
                          {g.wm_partno && <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }} data-testid="wm-part-no">WM# {g.wm_partno}</div>}
                        </td>
                        <td style={{ ...td, fontSize: 12 }}>{g.name || "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtNum(g.open_qty)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{g.open_value ? fmtCur(g.open_value, g.currency_code) : "—"}</td>
                        <td style={{ ...td, textAlign: "right", color: "#64748b" }}>{(g.lines || []).length}</td>
                      </tr>
                      {open[g.icode] && (g.lines || []).map((l, i) => (
                        <tr key={`${g.icode}-${i}`} style={{ background: "#f8fafc", fontSize: 12 }}>
                          <td style={td}></td>
                          <td style={td} className="text-mono">{l.customer_name}</td>
                          <td style={td} className="text-mono">PO {l.ms_pono} · {l.ms_custpo || ""}</td>
                          <td style={{ ...td, textAlign: "right" }}>{fmtNum(l.qty_open)}</td>
                          <td style={{ ...td, textAlign: "right", color: "#0f766e" }}>Target {fmtDate(l.target_date)}</td>
                          <td style={{ ...td, textAlign: "right", color: "#64748b" }}>Due {fmtDate(l.deliv_date)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {(data.by_item || []).length === 0 && (
                    <tr><td colSpan={6} className="muted" style={{ padding: 24, textAlign: "center" }}>Nothing open in this window.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!data && !loading && (
          <div className="muted" style={{ padding: 30, textAlign: "center" }}>Ask a question above, or set filters and hit Run.</div>
        )}
      </div>
    </Layout>
  );
}

const inp = { padding: "8px 10px", borderRadius: 8, border: "1px solid var(--omh-border,#cbd5e1)", fontSize: 13, minWidth: 160 };
const th = { padding: "9px 12px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" };
const td = { padding: "8px 12px", verticalAlign: "top" };

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>{label}</span>
      {children}
    </label>
  );
}
